<template>
  <div class="prose lg:prose-xl text-left w-full max-w-screen-xl mx-auto px-4">
    <div v-if="loading" class="italic text-center">
      <div class="font-semibold">{{ $t('feedback.loading') }}</div>
      <div class="text-sm">{{ $t('feedback.pleaseWait') }}</div>
    </div>
    <div :key="redrawCall" ref="editorRef" class=""></div>
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted, onUnmounted, watch, nextTick, computed } from 'vue'
import { useSystemStore } from '@/stores'
import { midiaService } from '@/services/midias.service'

import Editor from '@toast-ui/editor'
import colorSyntax from '@toast-ui/editor-plugin-color-syntax'
import tableMergedCell from '@toast-ui/editor-plugin-table-merged-cell'

import '@toast-ui/editor/dist/toastui-editor-viewer.css'
import '@toast-ui/editor/dist/theme/toastui-editor-dark.css'

const {
  initialValue = '',
  mediaId = ''
} = defineProps<{
  initialValue?: string,
  mediaId?: string
}>()

const editorRef = ref<HTMLElement>()
let viewer : any = null

const loading = ref(false)
const localContent = ref('')
const redrawCall = ref(0) // Serve para re-desenhar a tela quando muda o tema

const content = computed(() => localContent.value || initialValue)

// Métodos do editor
const destroy = () => {
  if (!viewer) return

  viewer.destroy()
  viewer = null
}

const init = () => {
  if (viewer) return

  viewer = Editor.factory({
    el: editorRef.value as HTMLElement,
    viewer: true,

    plugins: [colorSyntax, tableMergedCell],
    autofocus: false,
    language: 'en',
    useCommandShortcut: true,
    usageStatistics: false,
    theme: useSystemStore().isDark ? 'dark' : 'light'
  })

  if (content.value) {
    viewer.setMarkdown(content.value)
  } else if (mediaId && !loading.value) {
    loading.value = true
    midiaService.getFileInfo(mediaId).then(({ data }) => {
      localContent.value = data.freeContent || ''
      viewer.setMarkdown(localContent.value)
    }).catch(() => {
      viewer.setMarkdown('# Error\n\nUnable to load content.')
    }).finally(() => {
      loading.value = false
    })
  }
}

watch(
  () => useSystemStore().isDark,
  (nV, oV) => {
    if (nV === oV) return

    destroy()
    nextTick(() => {
      redrawCall.value += 1
      nextTick(init)
    })
  }
)

onMounted(() => {
  init()
})

onUnmounted(destroy)
</script>

<style scoped lang="scss">
.toast-editor-wrapper {
  @apply w-full;

  .toast-editor-container {
    @apply border border-gray-300 rounded-lg overflow-hidden;
    :deep(.toastui-editor) {
      border: none;
    }
  }

  :deep(.toastui-editor-popup-body input[type='text'].tui-colorpicker-palette-hex) {
    height: 32px !important;
    width: 92px !important;
  }
}
</style>
